// Copyright (c) StrangeBee 2024

import { Space } from 'antd';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocalMessage } from '@Helpers/';
import PreviewButton from '@Atoms/Buttons/PreviewButton/PreviewButton';
import RouteBuilder from '@Services/RouteBuilder';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useUserPermissions from '@Hooks/useUserPermissions';
import { AdminPermissionsEnum, OrganisationOutput, UserOutput } from 'thehive-sdk';
import { ADMIN_ORG_ID } from '@Types';
import { THForm } from '@Molecules/THForm/THForm';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { Form } from 'antd';
import { DisplayMetadata } from '@Molecules/DisplayMetadata/DisplayMetadata';
import Details from '@Molecules/Details/Details';
import { OrganisationEventList } from '@Organisms/Organisation/eventList';
import { FormatDate } from '@Molecules/Format';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { extractData } from './extractData';
import { getUpdatedAvatar } from '@Atoms/UploadAvatar/UploadAvatar';
import client, { queryClient } from '@Client';
import { computeEventList } from '@Organisms/User/eventList';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import useListQuery from '@Hooks/useListQuery';
import { CopyValue } from '@Atoms/CopyValue/CopyValue';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { useCurrentUser } from '@Queries/userQueries';
import { checkPermissions } from '@Helpers/permissions';
import { ICONS } from '@Constants/iconsApp';
import { computeListSelector } from '../selector';
import { useGenericQuery } from '@Queries/genericQueries';

import './OrganisationDrawer.scss';

interface OrganisationDrawerProps {
    eventList: OrganisationEventList;
    data: OrganisationOutput;
    triggerElem?: ReactElement;
    visible: boolean;
    onVisibleChange: (visible: boolean) => void;
}

export const OrganisationDrawer = ({
    data,
    triggerElem,
    eventList,
    visible,
    onVisibleChange,
}: OrganisationDrawerProps): ReactElement => {
    const i = useLocalMessage();

    const { name = '', _createdAt, _createdBy, _updatedAt, _id, links } = data || {};
    const [form] = Form.useForm();
    const { data: currentUser } = useCurrentUser();
    const { permissions = [] } = currentUser || {};
    const hasPermission = useUserPermissions({ permissionsNeeded: [AdminPermissionsEnum.MANAGE_ORGANISATION] });
    const canEditOrg = hasPermission && name !== ADMIN_ORG_ID;
    const [fieldsTouched, setFieldsTouched] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const { data: userList = [], refetch: refetchUserList } = useListQuery<UserOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'getOrganisation', idOrName: name }, { name: 'users' }],
                options: { organisation: name },
                name: `get-${name}-users`,
            },
            paginationOptions: {
                page: 0,
                itemsPerPage: 100,
            },
            sort: ['+login'],
        },
        controller: client.UserController,
        fetchOnStart: false,
        manual: true,
    });

    const { data: orgList = [] } = useListQuery<OrganisationOutput[]>({
        queryParams: {
            selector: computeListSelector(),
        },
        controller: client.OrganisationController,
        manual: true,
        fetchOnStart: true,
    });

    const { data: sharingProfileList = [] } = useGenericQuery({
        fetchCallback: () => client.OrganisationController.getSharingProfile(),
        key: ['get-organisation-sharing-profile'],
        manual: true,
        fetchOnStart: false,
    });

    const userEventList = useMemo(
        () =>
            computeEventList({
                locale: i,
                controller: client.UserController,
                onSuccess: (count, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                    refetchUserList();
                    queryClient.refetchQueries(['list-all-users']);
                },
                onError: handleNotificationError,
            }),
        [i, refetchUserList],
    );

    const forbiddenFields = useMemo(() => {
        const fields = [];

        if (name === 'admin') {
            fields.push('locked');
        }
        if (!checkPermissions({ permissions, permissionRequired: AdminPermissionsEnum.MANAGE_USER })) {
            fields.push('#computeUsers');
        }

        return fields;
    }, [name, permissions]);

    useEffect(() => {
        form.resetFields();
    }, [data, userList, form]);

    return (
        <div className="generic-drawer-preview">
            <THDrawer
                title={
                    <Space>
                        <ICONS.OrganisationIcon />
                        {i('organisation.preview')}
                    </Space>
                }
                triggerElem={triggerElem || <PreviewButton />}
                className="drawer-form"
                width="60vw"
                disableClose={fieldsTouched || drawerOpen}
                visible={visible}
                afterVisibleChange={(visible) => {
                    form.resetFields();
                    if (visible) {
                        refetchUserList();
                    }
                    onVisibleChange(visible);
                }}
            >
                <THForm<OrganisationOutput>
                    name="organisation-drawer-form"
                    form={form}
                    editable
                    submitTouchedValues
                    onValuesChange={() => {
                        setFieldsTouched(true);
                    }}
                    initialValues={data}
                    //eslint-disable-next-line
                    onFinish={({ _createdAt, _updatedAt, avatar, ...data }) => {
                        const processedAvatar = !avatar?.startsWith('api/v1') ? getUpdatedAvatar(avatar) : undefined;

                        eventList
                            .onEdit(
                                {
                                    ...data,
                                    avatar: processedAvatar,
                                },
                                name,
                            )
                            .then(() => {
                                if (processedAvatar) {
                                    queryClient.refetchQueries(['currentUser']);
                                }

                                if (data.name !== name) {
                                    refetchUserList();
                                }

                                if (data.locked !== undefined) {
                                    queryClient.refetchQueries(['currentUser']);
                                }
                            });

                        setFieldsTouched(false);
                    }}
                >
                    <div className="organisation-drawer">
                        <DisplayMetadata
                            items={[
                                {
                                    icon: <ICONS.SettingIcon />,
                                    label: 'id',
                                    value: <CopyValue value={_id}>{_id}</CopyValue>,
                                },
                                {
                                    icon: <ICONS.UserIcon />,
                                    label: i('common.created-by'),
                                    value: <AssigneeInfo assignee={_createdBy} noAvatar />,
                                },
                                {
                                    icon: <ICONS.CreationDateIcon />,
                                    label: i('common.created-at'),
                                    value: <FormatDate date={_createdAt} />,
                                },
                                {
                                    icon: <ICONS.UpdateDateIcon />,
                                    label: i('common.updated-at'),
                                    value: <FormatDate date={_updatedAt} />,
                                    hidden: !_updatedAt,
                                },
                            ]}
                        />
                        <div className="organisation-drawer__content">
                            <Details
                                data={{
                                    ...data,
                                    '#computeUsers': { userList, userEventList },
                                    '#computeLinks': { links, orgList, sharingProfileList },
                                }}
                                extract={extractData({
                                    locale: i,
                                    data,
                                    eventList,
                                    hasPermissions: canEditOrg,
                                    userList,
                                    userEventList,
                                    onDrawerVisible: (visible) => setDrawerOpen(visible),
                                })}
                                forbidden={forbiddenFields}
                            />
                        </div>

                        {fieldsTouched ? (
                            <FormSubmitButton
                                onCancel={() => {
                                    form.resetFields();
                                    setFieldsTouched(false);
                                }}
                                disabled={false}
                                footerText={i('general.confirm')}
                                hidden={!fieldsTouched}
                                formName="organisation-drawer-form"
                            />
                        ) : (
                            <THDrawer.Footer>
                                {
                                    <Link to={RouteBuilder.compute('administration.users', { orgId: name })}>
                                        <THButton type="primary">{i('common.goToDetails')}</THButton>
                                    </Link>
                                }
                            </THDrawer.Footer>
                        )}
                    </div>
                </THForm>
            </THDrawer>
        </div>
    );
};
